import { Button } from '@kerfjs/ui/button';
import { List } from '@kerfjs/ui/list';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { ProgressBar } from '@kerfjs/ui/progress-bar';
import { Row } from '@kerfjs/ui/row';
import { StateBanner } from '@kerfjs/ui/state-banner';
import { type SafeHtml } from 'kerfjs';
import { MessageCircleQuestionMark, RefreshCcwDot, StickyNote, Truck } from 'lucide';

import { CLARIFY_FORM_REASON } from '../../../../shared/contract/forms.js';
import {
  DEMAND_NOTES_SCOPE,
  type DemandNote,
  notesInScope,
  unreadNotes as unreadIn,
} from '../../../../shared/demand-notes.js';
import { canReopenDemand, managesDemands } from '../../../../shared/demand-transitions.js';
import { orderDay, type OrderProgress, orderProgress } from '../../../../shared/fulfilment.js';
import type { LineOrderT } from '../../../data/api.js';
import { currentPricingCheckState, pricingPromptDismissed } from '../../../data/pricing-prompt.js';
import {
  analysisList,
  attachmentErrorsOpen,
  editorSegment,
  type FormRow,
  type IntakeDoc,
  type IntakeLine,
  me,
  multifileUploadProgress,
  notice,
  quoteMatrix,
  workspace,
} from '../../../data/state.js';
import { attachmentProcessingState } from '../../../lib/attachment-processing.js';
import {
  demandDetailsReadOnly,
  demandIsCancelled,
  demandLockState,
  demandReadOnly,
  demandRejectionState,
  isRequester,
} from '../../../lib/context.js';
import {
  type ClarificationNote,
  lineClarificationReasons,
  pendingClarificationQuestion,
} from '../../../lib/line-status.js';
import { pricingPromptVisible } from '../../../lib/pricing-check-control.js';
import { AttachmentErrorsDialog } from './attachment-errors-dialog.js';

/** The requester's fulfilment headline (FR-5.18, FR-5.19). */
function fulfilmentTitle(progress: OrderProgress): string {
  if (progress.delivered >= progress.purchased)
    return progress.purchased === 1 ? 'Your item has been delivered.' : 'All items have been delivered.';
  if (progress.ordered >= progress.purchased)
    return progress.purchased === 1
      ? 'Your item has been ordered.'
      : `All ${String(progress.purchased)} items have been ordered.`;
  return `${String(progress.ordered)} of ${String(progress.purchased)} items ordered.`;
}

export function DemandEditorBanners(): SafeHtml {
  const ws = workspace.value;
  if (ws === null) return <></>;
  const readOnly = demandReadOnly();
  const cancelled = demandIsCancelled();
  const demand = ws.demand as { status?: string; cancelledFromStatus?: string | null } | undefined;
  const canReopen =
    cancelled && canReopenDemand('cancelled', demand?.cancelledFromStatus, managesDemands(me.value?.roles ?? []));
  const requester = isRequester();
  // Decided and closed demands are locked (HS2-Y50H8E). Staff see why, and reviewers and admins
  // can reopen one for changes, or lock it again once they are done.
  const lock = requester ? null : demandLockState(managesDemands(me.value?.roles ?? []));
  // Rejected lines on a finished demand can be reopened with an update (HS2-Z6MFSA).
  const rejection = demandRejectionState();
  const openForms = cancelled
    ? []
    : ((ws.forms ?? []) as FormRow[]).filter(
        (form) =>
          form.status === 'open' &&
          (form.schema.targetRole === undefined ||
            form.schema.targetRole === 'any' ||
            form.schema.targetRole === (requester ? 'requester' : 'reviewer')),
      );
  const processing = attachmentProcessingState((ws.filesByKind ?? {}) as Record<string, IntakeDoc[]>);
  // A reviewer's open question leads a flagged line's reasons (HS2-4YCV66).
  const notes = (ws.notes ?? []) as ClarificationNote[];
  const requesterMemberId = (ws.demand as { requesterMemberId?: string | null } | undefined)?.requesterMemberId;
  const warningLines = ((ws.lineItems ?? []) as IntakeLine[])
    .map((line) => ({
      line,
      reasons: lineClarificationReasons(
        line,
        line.needsClarification ? pendingClarificationQuestion(line.id, notes, requesterMemberId)?.body : undefined,
      ),
    }))
    .filter(({ reasons }) => reasons.length > 0);
  const firstWarningLine = warningLines[0]?.line;
  const hasNotice = !demandDetailsReadOnly() && notice.value !== '';
  // Unread demand notes by someone else (HS2-E33XQD): Review opens Documents, which reads them.
  const unreadNotes = editorSegment.value === 'files' ? [] : unreadDemandNotesIn(ws, me.value?.memberId);
  // The requester's main question once a demand is decided: is it ordered, and when does it arrive (FR-5.18)?
  const fulfilment = requester
    ? orderProgress(
        (ws.decisions ?? []) as { lineItemId: string; action: string | null; isCurrent: boolean }[],
        (ws.orders ?? []) as LineOrderT[],
        new Date().toISOString().slice(0, 10),
      )
    : null;
  const showFulfilment = fulfilment !== null && fulfilment.ordered > 0 && !cancelled;
  // Once a staff member can run the first pricing check, say so until they run it
  // or dismiss the prompt (HS2-CNXFTE). Summary's own empty state offers the run there.
  const demandId = (ws.demand as { id?: string } | undefined)?.id ?? '';
  const pricingControl = currentPricingCheckState('workspace');
  const onSummary = editorSegment.value === 'summary' || editorSegment.value === 'decision-summary';
  const showPricingPrompt =
    demandId !== '' &&
    !onSummary &&
    pricingPromptVisible({
      control: pricingControl,
      hasAnalysis: analysisList.value.length > 0,
      dismissed: pricingPromptDismissed(demandId),
    });
  const hasQuotes = (quoteMatrix.value?.columns.length ?? 0) > 0;
  if (
    !showFulfilment &&
    !showPricingPrompt &&
    !cancelled &&
    lock === null &&
    rejection === null &&
    unreadNotes.length === 0 &&
    openForms.length === 0 &&
    warningLines.length === 0 &&
    !hasNotice &&
    processing.active.length === 0 &&
    processing.failed.length === 0 &&
    multifileUploadProgress.value === null
  )
    return <></>;
  return (
    <List className="demand-editor-banners" gap="xs" controlInsets="b">
      {multifileUploadProgress.value !== null ? (
        <StateBanner
          tone="info"
          title={`Uploading ${String(multifileUploadProgress.value.uploaded)} of ${String(multifileUploadProgress.value.total)} Files`}
          detail="Reading and matching will continue after the upload."
        />
      ) : null}
      {lock !== null ? (
        <StateBanner
          tone={lock.tone}
          title={lock.title}
          detail={lock.detail}
          copyLayout="stacked"
          action={
            lock.action === null ? undefined : (
              <Button emphasis="primary" size="compact" tone="brand" action={lock.action.name}>
                {lock.action.label}
              </Button>
            )
          }
        />
      ) : null}
      {rejection !== null ? (
        <StateBanner
          tone="warning"
          copyLayout="stacked"
          title={
            rejection.rejected === rejection.total
              ? 'This demand was rejected.'
              : `${String(rejection.rejected)} of ${String(rejection.total)} lines were rejected.`
          }
          detail="If something changed or you can add information, reopen it with an update. The rejections are cleared and it goes back to the procurement team."
          action={
            rejection.canReopen ? (
              <Button emphasis="primary" size="compact" tone="brand" action="reopen-update-open">
                Reopen with Update…
              </Button>
            ) : undefined
          }
        />
      ) : null}
      {showFulfilment ? (
        <StateBanner
          tone={fulfilment.delivered >= fulfilment.purchased ? 'success' : 'info'}
          copyLayout="stacked"
          title={fulfilmentTitle(fulfilment)}
          detail={[
            fulfilment.delivered > 0 && fulfilment.delivered < fulfilment.purchased
              ? `${String(fulfilment.delivered)} of ${String(fulfilment.purchased)} delivered.`
              : '',
            fulfilment.nextExpected === null ? '' : `Next delivery expected ${orderDay(fulfilment.nextExpected)}.`,
            'Each line shows its purchase order.',
          ]
            .filter((part) => part !== '')
            .join(' ')}
          icon={<LucideIcon icon={Truck} name="" />}
        />
      ) : null}
      {unreadNotes.length > 0 ? (
        <StateBanner
          tone="info"
          title={unreadNotes.length === 1 ? 'New note on this demand' : 'New notes on this demand'}
          badge={String(unreadNotes.length)}
          detail={`From ${[...new Set(unreadNotes.map((note) => note.authorName ?? 'someone'))].join(', ')}.`}
          icon={<LucideIcon icon={StickyNote} name="" />}
          action={
            <Button emphasis="primary" size="compact" tone="brand" action="notes-review">
              Review
            </Button>
          }
        />
      ) : null}
      {cancelled ? (
        canReopen ? (
          <StateBanner
            tone="warning"
            title="This demand is cancelled."
            detail="It is read-only. Reopen it to make changes."
            action={
              <Button emphasis="primary" size="compact" tone="brand" action="reopen-demand">
                Reopen
              </Button>
            }
          />
        ) : (
          // A requester cannot reopen a demand staff cancelled (HS2-EY3F2G).
          <StateBanner tone="warning" title="This demand is cancelled." detail="It is read-only." />
        )
      ) : null}
      {showPricingPrompt ? (
        <StateBanner
          tone="success"
          className="pricing-check-prompt"
          title="Ready for a Pricing Check"
          copyLayout="stacked"
          icon={<LucideIcon icon={RefreshCcwDot} name="" />}
          detail={
            hasQuotes
              ? 'Benchmark each line against market prices and compare your quotations. Results appear on Summary.'
              : 'Get an expected price range for each line now, or add quotations first to compare them. Results appear on Summary.'
          }
          action={
            <Row gap="xs" vAlign="middle">
              <Button emphasis="plain" size="compact" tone="neutral" action="pricing-prompt-dismiss">
                Dismiss
              </Button>
              <Button emphasis="primary" size="compact" tone="success" action="run-analysis">
                Run Pricing Check
              </Button>
            </Row>
          }
        />
      ) : null}
      {openForms.length > 0 ? (
        <StateBanner
          tone="info"
          title={
            requester
              ? 'The procurement team needs a few answers'
              : openForms.every((form) => form.reason === CLARIFY_FORM_REASON)
                ? 'Optional questions can refine the pricing check'
                : 'The assistant has a few questions'
          }
          badge={String(openForms.length)}
          icon=<LucideIcon icon={MessageCircleQuestionMark} name="" />
          action={
            <Button size="compact" tone="brand" emphasis="primary" action="open-questions">
              Answer…
            </Button>
          }
        />
      ) : null}
      {!readOnly && warningLines.length > 0 ? (
        <StateBanner
          tone="danger"
          urgency="alert"
          title={`${String(warningLines.length)} line item${warningLines.length === 1 ? ' needs' : 's need'} attention.`}
          copyLayout="stacked"
          detail={warningLines
            .map(({ line, reasons }) => `Line ${String(line.lineNumber)}: ${reasons.join(' ')}`)
            .join(' ')}
          action={
            firstWarningLine === undefined ? undefined : (
              <Button
                tone="danger"
                emphasis="primary"
                size="compact"
                action="line-open"
                rootAttributes={{ 'data-line-id': firstWarningLine.id }}
              >
                {`Open Line ${String(firstWarningLine.lineNumber)}`}
              </Button>
            )
          }
        />
      ) : null}
      {!readOnly && processing.active.length > 0 ? (
        <div role="status" aria-label="Attachment processing progress">
          <List gap="xs">
            <StateBanner
              tone="info"
              title={`Processing ${String(processing.active.length)} attachment${processing.active.length === 1 ? '' : 's'}`}
              detail="Reading files and updating this demand."
            />
            <ProgressBar
              className="attachment-progress"
              value={(processing.progress ?? 0) / 100}
              ariaLabel="Attachment processing progress"
            />
          </List>
        </div>
      ) : null}
      {!readOnly && processing.failed.length > 0 ? (
        <StateBanner
          tone="danger"
          urgency="alert"
          title={`${String(processing.failed.length)} attachment${processing.failed.length === 1 ? '' : 's'} could not be processed.`}
          detail="Open the error details to review the affected files."
          action={
            <Button tone="neutral" emphasis="primary" size="compact" action="attachment-errors-open">
              View Errors…
            </Button>
          }
        />
      ) : null}
      {hasNotice ? <StateBanner tone="info" title={notice.value} /> : null}
      {attachmentErrorsOpen.value && processing.failed.length > 0 ? (
        <AttachmentErrorsDialog
          failed={processing.failed}
          canRetry={
            !readOnly &&
            (me.value?.roles ?? []).some((role) => role === 'procurement' || role === 'reviewer' || role === 'admin')
          }
        />
      ) : null}
    </List>
  );
}

/** Demand-level notes in a workspace that `memberId` has not read (HS2-E33XQD). */
function unreadDemandNotesIn(
  ws: { notes?: unknown; noteReads?: unknown },
  memberId: string | null | undefined,
): DemandNote[] {
  const notes = notesInScope((ws.notes ?? []) as DemandNote[], null);
  const readAt = ((ws.noteReads ?? {}) as Record<string, string>)[DEMAND_NOTES_SCOPE];
  return unreadIn(notes, readAt, memberId);
}
